import { useState } from "react";
import {
  HiChevronDown,
  HiChevronRight,
  HiOutlineExclamationCircle,
  HiOutlineTrash,
} from "react-icons/hi";
import MeritoButton from "../common/MeritoButtons/MeritoButtons";
import { PulseDocSummary } from "./types";
import {
  hasProcessingDocuments,
  isDocumentDeletable,
  isDocumentFailed,
  isDocumentPasswordRequired,
  pluralize,
} from "./utils";
import { DocumentList } from "./DocumentList";
import { AccountStatusRow, AccountStatusTone } from "./AccountStatusRow";
import { usePersistedExpansion } from "./usePersistedExpansion";
import { useBankStatementsActions } from "./BankStatementsContext";
import { getApiErrorMessage } from "../../utils/getApiErrorMessage";
import styles from "./BankStatements.module.scss";

export const UnidentifiedAccountCard = ({
  documents,
  companyId,
}: {
  documents: PulseDocSummary[];
  companyId: number;
}) => {
  const { expanded, toggleExpanded } = usePersistedExpansion(
    companyId,
    "new-account"
  );
  const { removeDocument, removeDocuments, submitDocumentPassword } =
    useBankStatementsActions();
  const [removingAll, setRemovingAll] = useState(false);
  const [removeError, setRemoveError] = useState("");
  const hasProcessingDocument = hasProcessingDocuments(documents);
  const hasTerminalFailure = documents.some(isDocumentFailed);
  const requiresPassword = documents.some(isDocumentPasswordRequired);
  const canRemoveAll =
    !!documents.length && documents.every(isDocumentDeletable);
  const fileLabel = `${pluralize(documents.length, "file")} uploaded`;
  const statusTone: AccountStatusTone = hasProcessingDocument
    ? "processing"
    : hasTerminalFailure || requiresPassword
    ? "failed"
    : null;

  const handleRemoveAll = async () => {
    if (!canRemoveAll || removingAll) return;
    setRemovingAll(true);
    setRemoveError("");
    try {
      await removeDocuments(
        documents.map((doc) => doc.id),
        true
      );
    } catch (error) {
      setRemoveError(
        getApiErrorMessage(error, "These statements could not be removed.")
      );
    } finally {
      setRemovingAll(false);
    }
  };

  return (
    <div className={`${styles.accountCard} ${styles.unidentifiedCard}`}>
      <div className={styles.accountHeader}>
        <div className={styles.newAccountIcon} aria-hidden="true">N</div>
        <div className={styles.accountIdentity}>
          <strong>New account</strong>
        </div>
        <div className={styles.accountActions}>
          {canRemoveAll ? (
            <span title="Delete all statements">
              <MeritoButton
                type="destructive-ghost"
                size="md2"
                onlyIcon
                hover={false}
                disabled={removingAll}
                loading={removingAll}
                icon={<HiOutlineTrash />}
                onClick={handleRemoveAll}
              />
            </span>
          ) : null}
          <MeritoButton
            type="tertiary"
            size="md2"
            onlyIcon
            hover={false}
            icon={expanded ? <HiChevronDown /> : <HiChevronRight />}
            onClick={toggleExpanded}
          />
        </div>
      </div>
      {removeError ? (
        <div className={styles.inlineError}>{removeError}</div>
      ) : null}
      {!expanded ? (
        <AccountStatusRow fileLabel={fileLabel} tone={statusTone} />
      ) : (
        <div className={styles.accountBody}>
          {hasTerminalFailure ? (
            <div className={styles.newAccountError}>
              <strong>
                <HiOutlineExclamationCircle />
                These files are from different bank accounts.
              </strong>
              <span>Add statements for one account at a time.</span>
            </div>
          ) : null}
          <DocumentList
            documents={documents}
            onRemove={(doc) => removeDocument(doc.id, true)}
            onSubmitPassword={async (doc, password, signal) => {
              await submitDocumentPassword(doc, password, {
                submitAfterSave: true,
                signal,
              });
            }}
          />
        </div>
      )}
    </div>
  );
};
